import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as L from 'leaflet';
import { useMap } from 'react-leaflet';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCC } from '../../contexts/CCContext';
import { createMapScope, viewport } from '../../services/CCMapGateway';
import { ccErrorCode } from '../../services/CCPublicationService';
import { authenticatedTiles } from './CCAuthenticatedTiles';
import useCCResource from './useCCResource';
import { cropLabel, cropStyle } from './publicationContract';
import { CCError } from './CCFeedback';
import { CC_MIN_VECTOR_ZOOM, CC_V2_MIN_TILE_ZOOM } from './mapZoom';
import './cc.css';

export default function CCPublicationOverlay() {
  const cc = useCC(); const map = useMap(); const { t, i18n } = useTranslation('translation');
  const [renewal, setRenewal] = useState(0); const [status, setStatus] = useState({});
  const select = useRef(cc.setSelectedPolygon); select.current = cc.setSelectedPolygon;
  const regionSelect = useRef(cc.navigateRegion); regionSelect.current = cc.navigateRegion;
  const selected = useRef(cc.selectedPolygon?.properties.fid); selected.current = cc.selectedPolygon?.properties.fid;
  const vectorRef = useRef(null); const vectorComplete = useRef(false); const retryResource = useRef(() => {});
  const panel = useRef(null); const focused = useRef(null);
  const mapKey = cc.mapQueryKey && cc.mode !== 'manage' ? `${cc.mapQueryKey}:${renewal}` : null;
  const result = useCCResource(mapKey, (signal) => createMapScope(cc.snapshot, cc.scope, cc.classCode, cc.permission.access_revision, signal, cc.excludedClassKeys));
  const scope = result.data;
  const { registerMap, reportMapLoading, mapQueryKey } = cc;
  useLayoutEffect(() => registerMap?.(), [registerMap]);
  const renew = () => setRenewal((value) => value + 1);
  useEffect(() => {
    if (!scope) return;
    const delay = Date.parse(scope.expires_at) - Date.now() - 10000;
    if (delay <= 0) return;
    const timer = setTimeout(() => setRenewal((value) => value + 1), delay);
    return () => clearTimeout(timer);
  }, [scope]);
  useEffect(() => {
    if (panel.current) { L.DomEvent.disableClickPropagation(panel.current); L.DomEvent.disableScrollPropagation(panel.current); }
  }, []);
  const countryOverview = cc.mode === 'explore' && cc.scope?.level === 'COUNTRY';
  const focusKey = JSON.stringify([cc.accessKey, cc.country, countryOverview ? cc.snapshot?.year : null,
    cc.snapshot?.boundary_revision_id, cc.scope?.kind, cc.scope?.subject_id, cc.scope?.membership_revision]);
  // Fit before attaching the replacement grid, so its first requests use the
  // overview extent instead of the previous year's zoomed-in viewport.
  useLayoutEffect(() => {
    if (!scope || focused.current === focusKey || !scope.bounds?.every(Number.isFinite)) return;
    focused.current = focusKey;
    const [west, south, east, north] = scope.bounds;
    const container = map.getContainer().getBoundingClientRect();
    const mobile = document.querySelector('.ci-mobile-panel')?.getBoundingClientRect();
    const legend = document.querySelector('.cc-legend')?.getBoundingClientRect();
    const bottom = mobile ? Math.max(20, container.bottom - mobile.top + 12) : Math.min(150, (legend?.height || 0) + 24);
    map.fitBounds([[south, west], [north, east]], { maxZoom: countryOverview ? 6 : 14, animate: false,
      paddingTopLeft: [20, 65], paddingBottomRight: [25, Math.min(bottom, container.height * 0.7)] });
  }, [scope, map, focusKey, countryOverview]);

  useEffect(() => {
    if (!cc.snapshot) return;
    vectorRef.current?.eachLayer((layer) => layer.setStyle({
      ...cropStyle(cc.snapshot, layer.feature), ...(!vectorComplete.current ? { fillOpacity: 0 } : {}),
      ...(layer.feature.properties.fid === selected.current ? { weight: 3, color: '#172f22' } : {}),
    }));
  }, [cc.selectedPolygon, cc.snapshot]);

  useLayoutEffect(() => {
    if (!scope || !cc.snapshot) return;
    let alive = true; let timer; let generation = 0; let vectors; let boundaries; let invalid = false; let viewMessage;
    const controllers = {};
    const resources = { raster: { pending: 0, errors: [] }, boundary: {}, vector: {} };
    const snapshot = cc.snapshot;
    const pane = map.getPane('cc-polygons') || map.createPane('cc-polygons'); pane.style.zIndex = '360';
    const boundaryPane = map.getPane('cc-regions') || map.createPane('cc-regions'); boundaryPane.style.zIndex = '350';
    const renderer = L.canvas({ padding: 0.2, pane: 'cc-polygons' });
    const update = (patch = {}) => {
      if (alive) setStatus({ key: mapKey, ...resources, complete: vectorComplete.current, message: viewMessage, ...patch });
    };
    const cancel = () => {
      generation += 1; Object.values(controllers).forEach((controller) => controller.abort()); clearTimeout(timer);
    };
    const clearLayers = () => {
      if (vectors) { map.removeLayer(vectors); vectors = null; vectorRef.current = null; }
      if (boundaries) { map.removeLayer(boundaries); boundaries = null; }
      vectorComplete.current = false;
    };
    const fallback = () => {
      vectorComplete.current = false; tiles.setOpacity(1);
      vectors?.eachLayer((layer) => layer.setStyle({ fillOpacity: 0 }));
    };
    const fatal = (error) => {
      const code = ccErrorCode(error);
      if (!alive || invalid) return;
      if (['ci_map_scope_not_found', 'ci_publication_not_found', 'ci_access_changed', 'ci_access_revoked', 'ci_identity_not_registered',
        'ci_organization_required', 'ci_forbidden'].includes(code) || [401, 403].includes(error.response?.status)) {
        invalid = true; cancel(); clearLayers(); map.removeLayer(tiles); select.current(null);
        resources.raster = { pending: 0, errors: [] }; resources.boundary = {}; resources.vector = {};
        update({ fatal: error });
      }
    };
    const tiles = authenticatedTiles(scope, fatal, (raster) => {
      if (!alive || invalid) return;
      resources.raster = raster; update();
    }).addTo(map);
    const expired = () => fatal({ code: 'ci_map_scope_not_found' });
    const expiryTimer = setTimeout(expired, Math.max(0, Date.parse(scope.expires_at) - Date.now()));
    const loadResource = async (resource) => {
      if (!alive || invalid) return;
      if (resource === 'boundary' && snapshot.rendering.show_boundaries === false) return;
      const zoom = map.getZoom();
      if (zoom < CC_V2_MIN_TILE_ZOOM) return;
      if (resource === 'vector' && zoom < CC_MIN_VECTOR_ZOOM) return;
      const b = map.getBounds(); const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
      if ((bbox[2] - bbox[0]) * (bbox[3] - bbox[1]) > 4) {
        resources[resource] = { message: 'ci.zoomForBoundaries' }; update(); return;
      }
      controllers[resource]?.abort();
      const controller = new AbortController(); controllers[resource] = controller;
      const { signal } = controller; const requestId = generation;
      const current = () => alive && !invalid && !signal.aborted && generation === requestId;
      resources[resource] = { loading: true }; update();
      try {
        const data = await viewport(scope, bbox, signal, resource === 'boundary'
          ? { boundaries: true, level: zoom >= 11 ? 'VILLAGE' : 'DISTRICT', budget: 2000 } : undefined);
        if (!current()) return;
        if (resource === 'boundary') {
          const next = L.geoJSON(data.features, { pane: 'cc-regions', style: { color: '#666666', weight: 1, dashArray: '3 3', fillOpacity: 0 },
            onEachFeature: (feature, layer) => {
              const label = document.createElement('span'); label.textContent = feature.properties.name || '';
              layer.bindTooltip(label);
              layer.on('click', () => {
                const r = feature.properties;
                regionSelect.current({ id: r.fid, name: r.name, level: r.level, parent_id: r.parent_id });
              });
            } }).addTo(map);
          if (boundaries) map.removeLayer(boundaries); boundaries = next;
        } else {
          vectorComplete.current = !data.incomplete;
          const style = (feature) => ({
            ...cropStyle(snapshot, feature), ...(!vectorComplete.current ? { fillOpacity: 0 } : {}),
            ...(feature.properties.fid === selected.current ? { weight: 3, color: '#172f22' } : {}),
          });
          const next = L.geoJSON(data.features, { renderer, pane: 'cc-polygons', style,
            onEachFeature: (feature, layer) => {
              const text = document.createElement('span');
              const entry = snapshot.classes.find((c) => c.code === feature.properties.crop_code);
              text.textContent = `${cropLabel(entry, t, i18n.language)} · ${snapshot.year} · ${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(feature.properties.area_ha)} ha`;
              layer.bindTooltip(text, { sticky: true });
              layer.on('mouseover', () => layer.setStyle({ weight: 2 }));
              layer.on('mouseout', () => layer.setStyle(style(feature)));
              layer.on('click', () => select.current(feature));
            } }).addTo(map);
          if (vectors) map.removeLayer(vectors); vectors = next; vectorRef.current = vectors;
          tiles.setOpacity(vectorComplete.current ? 0 : 1);
        }
        resources[resource] = { message: data.incomplete ? 'cc.incomplete' : resource === 'vector' && !data.features.length ? 'cc.emptyViewport' : null };
        update();
      } catch (error) {
        if (!current()) return;
        fatal(error);
        if (invalid) return;
        if (resource === 'vector') fallback();
        resources[resource] = { error }; update();
      }
    };
    const start = () => {
      if (invalid) return;
      cancel(); fallback(); viewMessage = null; resources.boundary = {}; resources.vector = {}; update();
    };
    const load = () => {
      if (invalid) return;
      const zoom = map.getZoom();
      if (zoom < CC_MIN_VECTOR_ZOOM && vectors) { map.removeLayer(vectors); vectors = null; vectorRef.current = null; }
      if (zoom < CC_V2_MIN_TILE_ZOOM) {
        clearLayers(); viewMessage = 'ci.zoomForCrops'; update(); return;
      }
      const b = map.getBounds();
      if ((b.getEast() - b.getWest()) * (b.getNorth() - b.getSouth()) > 4) clearLayers();
      loadResource('boundary'); loadResource('vector');
    };
    const schedule = () => { start(); if (!invalid) timer = setTimeout(load, 200); };
    retryResource.current = (resource) => {
      if (resource === 'raster') tiles.retryFailed();
      else loadResource(resource);
    };
    vectorComplete.current = false;
    map.on('movestart zoomstart', start); map.on('moveend zoomend', schedule); load();
    return () => {
      alive = false; cancel(); clearTimeout(expiryTimer); clearLayers(); map.removeLayer(tiles);
      retryResource.current = () => {};
      if (map.hasLayer(renderer)) map.removeLayer(renderer);
      map.off('movestart zoomstart', start); map.off('moveend zoomend', schedule);
    };
  }, [map, scope, cc.snapshot, mapKey, t, i18n.language]);
  const visible = status.key === mapKey ? status : {};
  const loading = result.status === 'loading' || (!!scope && status.key !== mapKey) ||
    (!visible.complete && visible.raster?.pending > 0) || visible.boundary?.loading || visible.vector?.loading;
  useEffect(() => {
    reportMapLoading?.(mapQueryKey, !!loading);
  }, [reportMapLoading, mapQueryKey, loading]);
  const error = result.error || visible.fatal;
  const resourceErrors = [['raster', !visible.complete && visible.raster?.errors?.[0]], ['boundary', visible.boundary?.error], ['vector', visible.vector?.error]];
  return <div ref={panel} className="cc-map-status ci-map-status" role="status">
    {cc.snapshot && <details><summary>{cc.snapshot.year} · {t('general.crop_intelligence')}</summary><div>{cc.snapshot.title}</div></details>}
    {loading && <div>{t('cc.loading')}</div>}
    {[...new Set([visible.message, visible.boundary?.message, visible.vector?.message].filter(Boolean))].map((key) => <div key={key}>{t(key)}</div>)}
    {error && <CCError error={error} retry={ccErrorCode(error) === 'ci_map_scope_not_found' || result.error ? renew : cc.retry} />}
    {!error && resourceErrors.map(([resource, failure]) => failure && <div key={resource}>
      <span>{t(`ci.mapResource${resource}`)}</span><CCError error={failure} retry={() => retryResource.current(resource)} />
    </div>)}
    {!error && scope && Date.parse(scope.expires_at) <= Date.now() && <Button onClick={renew}>{t('ci.scopeExpired')}</Button>}
  </div>;
}
